import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { RHFAutocomplete, RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { InputAdornment, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import * as yup from 'yup';
import { blockInvalidChar } from 'src/helper/blockInvalidCharNumFld';

export default function CandNewEditExpCompo({ desgData, cntryData, filterCurrency, switchState, onEmptyExperience, fromInterviewList }) {
  const genderArr = ['Male', 'Female'];
  // const [genderArr, setGenderArr] = useState([]);
  const { control, setValue, watch, resetField, trigger, getValues } = useFormContext();
  const watchedExp = watch('candExp');

  const subscr = JSON.parse(localStorage.getItem('selectedSubscrSite'));

  // Set default currency
  const cur = subscr?.country;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'candExp',
  });

  const handleAdd = () => {
    append({
      expSrno: 0,
      compNm: '',
      designation: null,
      ctc: 0,
      ctccurncy: cur || null, // Set currency from props if available
      country: null,
      currentywrkng: false,
      expfromDate: null,
      exptoDate: null,
    });
  };

  const handleRemove = (index) => {
    remove(index);
  };

  // Only call onEmptyExperience() after mount and update, not on initial render.to prevent incosistency 
  const hasMounted = useRef(false);

  useEffect(() => {
    if (hasMounted.current && fields.length === 0) {
      onEmptyExperience?.();
    }
    hasMounted.current = true;
  }, [fields.length, onEmptyExperience]);


  return (
    <>
      <Box
        rowGap={3}
        columnGap={2}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
        }}
        mb={3}
      >
        <Typography variant="h6" sx={{ color: 'text.disabled' }}>
          Experience Details:
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-end', md: 'center' }}
          justifyContent="flex-end"
        >
          <Button
            size="small"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleAdd}
            sx={{ flexShrink: 0, minWidth: '140px', justifyContent: 'flex-start' }}
          >
            Add Experience
          </Button>
        </Stack>
      </Box>
      <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />} spacing={3}>
        {fields.map((item, index) => (
          <Stack key={item.id} spacing={1.5}>
            <Typography variant="h6" sx={{ color: 'primary', mb: 2 }}>
              Experience {index + 1}:
            </Typography>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'none' } }}>
                <RHFTextField name={`candExp[${index}].expSrno`} />
              </Box>
              <RHFTextField
                name={`candExp[${index}].compNm`}
                label={
                  <div>
                    Employer Name<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                  </div>
                }
                placeholder="Employer Name"
              />

              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`candExp[${index}].designation`}
                  placeholder="Designation"
                  label={
                    <div>
                      Designation<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  options={desgData}
                  showPlusIcon
                  getOptionLabel={(option) => option.masterName}
                  //   value={selectedGender}
                  //   onChange={(event, newValue) => {
                  //     setSelectedGender(newValue);
                  //   }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.masterId}>
                      {option.masterName}
                    </li>
                  )}
                />
              </Stack>
              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`candExp[${index}].country`}
                  placeholder="Employer Country"
                  label="Employer Country"
                  options={cntryData}
                  getOptionLabel={(option) => option.countryName}
                  //   value={selectedGender}
                  onChange={(event, newValue) => {
                    setValue(`candExp[${index}].country`, newValue, { shouldValidate: true });
                    setValue(`candExp[${index}].ctccurncy`, newValue, { shouldValidate: true });
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.countriesId}>
                      {option.countryName}
                    </li>
                  )}
                />
              </Stack>
              {fromInterviewList === false && (
                <RHFTextField
                  name={`candExp[${index}].ctc`}
                  label="CTC"
                  placeholder="0.00"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                          <RHFAutocomplete
                            name={`candExp[${index}].ctccurncy`}
                            // defaultValue={{
                            //   currencySymbol: '₹', // Default symbol for Indian currency
                            //   countryName: 'India',  // Default country name
                            // }}
                            filterOptions={filterCurrency}
                            sx={{
                              '& fieldset': { border: 'none' },
                            }}
                            options={cntryData}
                            disableClearable
                            getOptionLabel={(option) => option.currencySymbol}
                            componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                            // value={selectedCurSalCer}
                            onChange={(event, value) => {
                              setValue(`candExp[${index}].ctccurncy`, value, {
                                shouldValidate: true,
                              });
                              trigger(`candExp[${index}].ctc`);
                            }}
                            renderOption={(props, option) => (
                              <li {...props} key={option.shortName}>
                                {option.countryName}({option.currencySymbol})
                              </li>
                            )}
                          />
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>
            <RHFCheckbox
              name={`candExp[${index}].currentywrkng`}
              label="Currently Working"
              labelPlacement="start"
              onChange={(e) => {
                const isChecked = e.target.checked;

                // Update the field value
                setValue(`candExp[${index}].currentywrkng`, isChecked);

                trigger(`candExp[${index}].exptoDate`);
                trigger(`candExp[${index}].expfromDate`);
              }}
              sx={{
                '&.MuiFormControlLabel-labelPlacementStart': {
                  margin: 0,
                },
              }}
            />
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Controller
                name={`candExp[${index}].expfromDate`}
                control={control}
                rules={{
                  validate: (fromDateValue, formValues) => {
                    if (!fromDateValue) {
                      return 'From date is required';
                    }
                    const toDateValue = formValues?.candExp?.[index]?.exptoDate ?? getValues(`candExp[${index}].exptoDate`);
                    if (!toDateValue) return true;

                    const parseDateSafe = (val) => {
                      if (!val) return null;
                      if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : new Date(val);
                      if (typeof val === 'string') {
                        let d = new Date(val);
                        if (!Number.isNaN(d.getTime())) return d;
                        const parts = val.split(/[-/]/);
                        if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
                           d = new Date(parts[2], parts[1] - 1, parts[0]);
                           if (!Number.isNaN(d.getTime())) return d;
                        }
                      }
                      if (val && typeof val.toDate === 'function') {
                         const d = val.toDate();
                         if (!Number.isNaN(d.getTime())) return d;
                      }
                      return null;
                    };

                    const fromDateNorm = parseDateSafe(fromDateValue);
                    const toDateNorm = parseDateSafe(toDateValue);

                    if (!fromDateNorm) return 'Invalid From Date';
                    if (!toDateNorm) return true;

                    fromDateNorm.setHours(0, 0, 0, 0);
                    toDateNorm.setHours(0, 0, 0, 0);

                    if (fromDateNorm >= toDateNorm) {
                      return 'From date must be less than To date';
                    }

                    return true;
                  }
                }}
                render={({ field, fieldState: { error } }) => {
                  let value = field.value;
                  if (value) {
                    const parsedValue = new Date(value);
                    if (!Number.isNaN(parsedValue.getTime())) {
                      value = parsedValue;
                    } else {
                      value = null;
                    }
                  } else {
                    value = null;
                  }

                  return (
                    <DatePicker
                      label={
                        <div>
                          From Date<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                      value={value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                        setTimeout(() => {
                          trigger(`candExp[${index}].expfromDate`); // Trigger validation for "From Date"
                          trigger(`candExp[${index}].exptoDate`); // Trigger validation for "To Date"
                          // NOTE: intentionally not calling trigger('candExp') here.
                          // That validates the WHOLE candExp array (every entry,
                          // every field — including empty required compNm/
                          // designation on this or other rows) and, because RHF's
                          // resolver-based trigger() replaces the entire error
                          // subtree at the given path, it was flooding unrelated
                          // fields with "required" errors the moment any date was
                          // touched.
                        }, 0);
                      }}
                     
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          onKeyUp: () => {
                            trigger(`candExp[${index}].expfromDate`);
                            trigger(`candExp[${index}].exptoDate`);
                          },
                        },
                      }}
                      maxDate={new Date()}
                    />
                  );
                }}
              />
              <Controller
                name={`candExp[${index}].exptoDate`}
                control={control}
                rules={{
                  validate: (toDateValue, formValues) => {
                    const isCurrentlyWorking = formValues?.candExp?.[index]?.currentywrkng ?? getValues(`candExp[${index}].currentywrkng`);
                    
                    if (!isCurrentlyWorking && !toDateValue) {
                      return 'To date is required';
                    }
                    if (!toDateValue) {
                      return true;
                    }

                    const fromDateValue = formValues?.candExp?.[index]?.expfromDate ?? getValues(`candExp[${index}].expfromDate`);

                    const parseDateSafe = (val) => {
                      if (!val) return null;
                      if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : new Date(val);
                      if (typeof val === 'string') {
                        let d = new Date(val);
                        if (!Number.isNaN(d.getTime())) return d;
                        const parts = val.split(/[-/]/);
                        if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
                           d = new Date(parts[2], parts[1] - 1, parts[0]);
                           if (!Number.isNaN(d.getTime())) return d;
                        }
                      }
                      if (val && typeof val.toDate === 'function') {
                         const d = val.toDate();
                         if (!Number.isNaN(d.getTime())) return d;
                      }
                      return null;
                    };

                    const toDateNorm = parseDateSafe(toDateValue);
                    const fromDateNorm = parseDateSafe(fromDateValue);

                    if (!toDateNorm) return 'Invalid To Date';

                    if (fromDateNorm) {
                      toDateNorm.setHours(0, 0, 0, 0);
                      fromDateNorm.setHours(0, 0, 0, 0);
                      if (toDateNorm <= fromDateNorm) {
                        return 'To date must be greater than From date';
                      }

                      const oneMonthLater = new Date(fromDateNorm);
                      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
                      if (toDateNorm < oneMonthLater) {
                        return 'To Date should be at least 1 month after From Date';
                      }
                    }

                    if (isCurrentlyWorking) {
                      // To Date should be maximum = currentDate + 3 months
                      const maxAllowedDate = new Date();
                      maxAllowedDate.setMonth(maxAllowedDate.getMonth() + 3);
                      maxAllowedDate.setHours(23, 59, 59, 999);
                      
                      toDateNorm.setHours(0, 0, 0, 0);
                      if (toDateNorm > maxAllowedDate) {
                        return 'To date cannot be more than 3 months in the future';
                      }
                    } else {
                      // If not currently working, toDate should be maximum today date
                      const today = new Date();
                      today.setHours(23, 59, 59, 999);
                      
                      toDateNorm.setHours(0, 0, 0, 0);
                      if (toDateNorm > today) {
                        return 'To date cannot be in the future';
                      }
                    }

                    return true;
                  },
                }}

                render={({ field, fieldState: { error } }) => {
                  const isCurrentlyWorking = watch(`candExp[${index}].currentywrkng`);

                  // Safely parse the value to a valid date or null
                  let value = field.value;
                  if (value) {
                    const parsedValue = new Date(value);
                    if (!Number.isNaN(parsedValue.getTime())) {
                      value = parsedValue;
                    } else {
                      value = null;
                    }
                  } else {
                    value = null;
                  }

                  const toDateMax = isCurrentlyWorking
                    ? new Date(new Date().setMonth(new Date().getMonth() + 3))
                    : new Date();

                  return (
                    <DatePicker
                      label={
                        <div>
                          To Date
                          {!isCurrentlyWorking && (
                            <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                          )}
                        </div>
                      }
                      value={value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                        setTimeout(() => {
                          trigger(`candExp[${index}].expfromDate`);
                          trigger(`candExp[${index}].exptoDate`);
                          // See note above: not calling trigger('candExp') —
                          // it re-validates every field in every row.
                        }, 0);
                      }}
                     
                      maxDate={toDateMax}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          // Force-hide the error whenever "currently working" is
                          // checked, at render time — see the matching comment
                          // in cand-newEdit-edu-compo.js for why this is needed
                          // (async resolver clearing timing).
                          error: isCurrentlyWorking ? false : !!error,
                          helperText: isCurrentlyWorking ? '' : error?.message,
                          placeholder: '', // To prevent "DD/MM/YYYY" placeholder
                          onKeyUp: () => {
                            trigger(`candExp[${index}].expfromDate`);
                            trigger(`candExp[${index}].exptoDate`);
                          },
                        },
                      }}
                    />
                  );
                }}
              />

            </Box>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-end', md: 'center' }}
              justifyContent="flex-end"
            >
              <Button
                size="small"
                color="error"
                startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                onClick={() => handleRemove(index)}
              >
                Remove
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </>
  );
}
CandNewEditExpCompo.propTypes = {
  desgData: PropTypes.array,
  cntryData: PropTypes.array,
  filterCurrency: PropTypes.func,
  switchState: PropTypes.object,
  onEmptyExperience: PropTypes.func,
  fromInterviewList: PropTypes.bool,
  // trigger: PropTypes.func,
};